import { createFileRoute } from "@tanstack/react-router"
import { PropsWithChildren } from "react"
import { Trans, useTranslation } from "react-i18next"

import { IconName } from "@/assets/icons"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import { DEFAULT_HELP_EMAIL, DEFAULT_HELP_PHONE } from "@/utilities/globals"
import { useTheme } from "@/utilities/hooks/useTheme"
import { normalizePhoneNumberWithCountryCode } from "@/utilities/normalizers"

export const Route = createFileRoute("/help")({
  component: HelpRouteComponent,
})

export function HelpRouteComponent() {
  const { t } = useTranslation()
  const support = useTheme().scannedArea?.theme.locationInformation.support
  const email = support?.email || DEFAULT_HELP_EMAIL
  const phone = normalizePhoneNumberWithCountryCode(
    support?.phone || DEFAULT_HELP_PHONE,
    "US"
  )
  const href = `mailto:${email}`
  const tel = `tel:${support?.phone || DEFAULT_HELP_PHONE}`

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("help.header")}
      </Heading>
      <HelpCard supportType="onsite">
        <Trans
          i18nKey="help.onsiteDescription"
          values={{
            onsiteHelpTitle: support?.onsiteHelperTitle || "Parking Ambassador",
          }}
        >
          Seek out a <span>{support?.onsiteHelperTitle}</span> for immediate
          assistance.
        </Trans>
      </HelpCard>
      <HelpCard supportType="email">
        <Trans i18nKey="help.email" values={{ email }}>
          You can email us at <a href={href}>{email}</a>
        </Trans>
      </HelpCard>
      <HelpCard supportType="phone">
        <Trans
          i18nKey="help.phone"
          values={{
            phone,
          }}
        >
          You can call us at <a href={tel}>{phone}</a>
        </Trans>
      </HelpCard>
    </>
  )
}

interface HelpCellProps {
  supportType: "onsite" | "phone" | "email"
}

function HelpCard(props: PropsWithChildren<HelpCellProps>) {
  const { t } = useTranslation()
  return (
    <Card>
      <address className="help-card">
        <div className="help-card--icon">
          <Icon size="default" name={iconFor()} />
        </div>
        <Heading level={3} as={2} style={{ marginTop: "var(--space-5)" }}>
          {titleFor()}
        </Heading>
        <Text className="medium" style={{ marginTop: "var(--space-1)" }}>
          {props.children}
        </Text>
      </address>
    </Card>
  )

  function iconFor(): IconName {
    switch (props.supportType) {
      case "onsite":
        return "person"
      case "phone":
        return "phone"
      case "email":
        return "mail"
    }
  }

  function titleFor() {
    switch (props.supportType) {
      case "onsite":
        return t("help.onsiteSubhead")
      case "phone":
        return t("help.phoneSubhead")
      case "email":
        return t("help.emailSubhead")
    }
  }
}
